import { useState } from "react";
import { deploy3rdPartyModel } from "../../utils/fine-tuning-functions";

export default function DeployThirdPartyModel() {
  const [modelData, setModelData] = useState({
    huggingfaceModelName: "",
    displayName: "",
    versionNumber: "",
    description: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setModelData((prevModelData) => ({
      ...prevModelData,
      [id]: value
    }));

    // Clear messages when user starts typing again
    if (success || error) {
      setSuccess(false);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);
      setError(null);
      setSuccess(false);

      // This function would need to be implemented in your utils/fine-tuning-functions.js
      await deploy3rdPartyModel(modelData);

      setSuccess(true);
      setModelData({
        huggingfaceModelName: "",
        displayName: "",
        versionNumber: "",
        description: ""
      });
    } catch (error) {
      console.error("Error registering model:", error);
      setError("Failed to register model. Please check the model name and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <header className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-ktx-purple">
          Register Third-Party Model
        </h1>
        <p className="mt-2 text-gray-600">
          Register a model from Hugging Face to use in your applications
        </p>
      </header>

      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Hugging Face Model Name Input */}
          <div>
            <label htmlFor="huggingfaceModelName" className="block text-sm font-medium text-gray-700 mb-1">
              Hugging Face Model Name
            </label>
            <input
              type="text"
              id="huggingfaceModelName"
              value={modelData.huggingfaceModelName}
              onChange={handleInputChange}
              required
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple sm:text-sm"
              placeholder="e.g., meta-llama/Llama-2-7b-chat-hf"
            />
            <p className="mt-1 text-xs text-gray-500">
              Enter the full repository name from Hugging Face (organization/model-name)
            </p>
          </div>

          {/* Display Name Input */}
          <div>
            <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-1">
              Display Name
            </label>
            <input
              type="text"
              id="displayName"
              value={modelData.displayName}
              onChange={handleInputChange}
              required
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple sm:text-sm"
              placeholder="Enter a friendly name for this model"
            />
            <p className="mt-1 text-xs text-gray-500">
              This name will be displayed in the UI and used to identify the model
            </p>
          </div>

          {/* Version Number Input */}
          <div>
            <label htmlFor="versionNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Version Number
            </label>
            <input
              type="text"
              id="versionNumber"
              value={modelData.versionNumber}
              onChange={handleInputChange}
              required
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple sm:text-sm"
              placeholder="e.g., 1.0.0"
            />
            <p className="mt-1 text-xs text-gray-500">
              Specify the version of the model you're registering (semantic versioning recommended)
            </p>
          </div>

          {/* Model Description Input */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Model Description
            </label>
            <textarea
              id="description"
              value={modelData.description}
              onChange={handleInputChange}
              required
              rows="4"
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple sm:text-sm"
              placeholder="Describe the model's capabilities, use cases, and any special requirements"
            />
            <p className="mt-1 text-xs text-gray-500">
              A detailed description will help users understand when to use this model
            </p>
          </div>

          {/* Success message */}
          {success && (
            <div className="rounded-md bg-green-50 p-4 border border-green-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700">
                    Model registered successfully! It can now be used in your applications.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="rounded-md bg-red-50 p-4 border border-red-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Submit button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ktx-purple ${isSubmitting ? 'bg-ktx-purple-light cursor-not-allowed' : 'bg-ktx-purple hover:bg-ktx-purple-dark'
                }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Registering...
                </>
              ) : (
                'Register Model'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Information card */}
      <div className="mt-8 bg-white p-5 rounded-lg shadow-sm border border-gray-200">
        <h4 className="font-medium text-ktx-purple flex items-center mb-4">
          <svg className="h-5 w-5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Third-Party Model Guidelines
        </h4>
        <div className="text-sm text-gray-600 space-y-3">
          <p>When registering a third-party model from Hugging Face:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Verify the model is compatible with your infrastructure</li>
            <li>Test the model thoroughly before using it in production</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

